import React from "react";
import PropTypes from "prop-types";

import { ROUND_SIZES, THEMES, THEME_LABELS } from "./options";

// Purple for feminine, not pink: the pink sat too close to the red used for a
// wrong answer, so the two read as the same signal at a glance. This purple is
// also light enough to clear 3:1 on the dark-mode background, which a deeper one
// doesn't - the noun is rendered in it.
export const DEFAULT_COLORS = { f: "#a032b8", m: "#1565c0" };

export default function Settings({
  audioAvailable,
  autoAdvance,
  roundSize,
  autoplayAudio,
  background,
  soundEffects,
  colors,
  colorCoding,
  systemDark,
  theme,
  loopAudio,
  onSetColor,
  onSetTheme,
  onSetRoundSize,
  onToggleAutoAdvance,
  onToggleAutoplayAudio,
  onToggleBackground,
  onToggleSoundEffects,
  onToggleColorCoding,
  onToggleLoopAudio,
  onToggleShuffle,
  shuffle,
}) {
  return (
    <div className="fna-settings">
      <label className="fna-setting" htmlFor="fna-round-size">
        Round length
        <select
          className="fna-setting-select"
          id="fna-round-size"
          onChange={(e) => onSetRoundSize(Number(e.target.value))}
          value={roundSize}
        >
          {ROUND_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} words
            </option>
          ))}
        </select>
      </label>

      <label className="fna-setting" htmlFor="fna-shuffle">
        <input checked={shuffle} id="fna-shuffle" onChange={onToggleShuffle} type="checkbox" />
        Shuffle
        <span className="fna-setting-note">
          {shuffle ? "random order" : "most common first"} · restarts the round
        </span>
      </label>

      <label className="fna-setting" htmlFor="fna-auto-advance">
        <input
          checked={autoAdvance}
          id="fna-auto-advance"
          onChange={onToggleAutoAdvance}
          type="checkbox"
        />
        Move on automatically
        <span className="fna-setting-note">when you're right</span>
      </label>

      <label className="fna-setting" htmlFor="fna-sound-effects">
        <input
          checked={soundEffects}
          id="fna-sound-effects"
          onChange={onToggleSoundEffects}
          type="checkbox"
        />
        Sound effects
        <span className="fna-setting-note">on a correct answer</span>
      </label>

      {audioAvailable && (
        <>
          <label className="fna-setting" htmlFor="fna-autoplay">
            <input
              checked={autoplayAudio}
              id="fna-autoplay"
              onChange={onToggleAutoplayAudio}
              type="checkbox"
            />
            Play audio after answering
          </label>

          <label className="fna-setting" htmlFor="fna-loop-audio">
            <input
              checked={loopAudio}
              id="fna-loop-audio"
              onChange={onToggleLoopAudio}
              type="checkbox"
            />
            Loop the audio
          </label>
        </>
      )}

      <div className="fna-setting">
        <label htmlFor="fna-theme">Theme</label>
        <select
          className="fna-setting-select"
          id="fna-theme"
          onChange={(e) => onSetTheme(e.target.value)}
          value={theme}
        >
          {THEMES.map((option) => (
            <option key={option} value={option}>
              {THEME_LABELS[option]}
            </option>
          ))}
        </select>
        {theme === "auto" && (
          <span className="fna-setting-note">
            following your system ({systemDark ? "dark" : "light"})
          </span>
        )}
      </div>

      <label className="fna-setting" htmlFor="fna-background">
        <input
          checked={background}
          id="fna-background"
          onChange={onToggleBackground}
          type="checkbox"
        />
        Background image
      </label>

      <label className="fna-setting" htmlFor="fna-color-coding">
        <input
          checked={colorCoding}
          id="fna-color-coding"
          onChange={onToggleColorCoding}
          type="checkbox"
        />
        Color code by gender
      </label>

      {colorCoding && (
        <div className="fna-colors">
          {[["m", "masculine"], ["f", "feminine"]].map(([gender, name]) => (
            <label className="fna-color" htmlFor={`fna-color-${gender}`} key={gender}>
              <input
                id={`fna-color-${gender}`}
                onChange={(e) => onSetColor(gender, e.target.value)}
                type="color"
                value={colors[gender]}
              />
              <span style={{ color: colors[gender] }}>{name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

Settings.propTypes = {
  audioAvailable: PropTypes.bool.isRequired,
  autoAdvance: PropTypes.bool.isRequired,
  roundSize: PropTypes.number.isRequired,
  autoplayAudio: PropTypes.bool.isRequired,
  background: PropTypes.bool.isRequired,
  soundEffects: PropTypes.bool.isRequired,
  colorCoding: PropTypes.bool.isRequired,
  colors: PropTypes.shape({ m: PropTypes.string, f: PropTypes.string }).isRequired,
  systemDark: PropTypes.bool.isRequired,
  theme: PropTypes.oneOf(THEMES).isRequired,
  loopAudio: PropTypes.bool.isRequired,
  onSetColor: PropTypes.func.isRequired,
  onSetRoundSize: PropTypes.func.isRequired,
  onToggleAutoAdvance: PropTypes.func.isRequired,
  onToggleAutoplayAudio: PropTypes.func.isRequired,
  onToggleBackground: PropTypes.func.isRequired,
  onToggleSoundEffects: PropTypes.func.isRequired,
  onToggleColorCoding: PropTypes.func.isRequired,
  onSetTheme: PropTypes.func.isRequired,
  onToggleLoopAudio: PropTypes.func.isRequired,
  onToggleShuffle: PropTypes.func.isRequired,
  shuffle: PropTypes.bool.isRequired,
};
